import * as React from 'react';
import { Activity, Ban, Bell, Clock, RefreshCw, Zap } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { StatCard } from '@/components/app/StatCard';
import { TimeseriesChart } from '@/components/app/TimeseriesChart';
import { StackedBarChart } from '@/components/app/StackedBarChart';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/app/EmptyState';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { humanizeCount, humanizePercent } from '@/lib/utils';
import { humanizeError } from '@/lib/apiClient';
import { CronHeartbeatStrip } from '../components/CronHeartbeatStrip';
import { DecisionsBrowser } from '../components/DecisionsBrowser';
import { KpiCards } from '../components/KpiCards';
import {
  useDashboardSnapshot,
  useDecisionsTimeseries,
  useRefreshDashboardSnapshot,
} from '../hooks';
import type { NotificationDashboardSnapshot } from '../types';
import type { DecisionsTimeseriesDay } from '../api/notificationsApi';

export function HealthDashboardPage() {
  const metrics = useDashboardSnapshot();
  // Live aggregate from notification_decisions — bypasses the rollup snapshots
  // which can be sparse in dev/early prod.
  const timeseries = useDecisionsTimeseries({ days: 14 });
  const refresh = useRefreshDashboardSnapshot();

  const snapshot = metrics.data?.snapshot ?? null;
  const computedAt = snapshot?.computedAt;
  const staleMinutes = computedAt
    ? Math.round((Date.now() - new Date(computedAt).getTime()) / 60_000)
    : null;
  const isStale = staleMinutes !== null && staleMinutes > 30;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications · Health"
        description="Rollup every 15 min + live decision stream. Click through cells for drill-down."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {computedAt && (
              <Badge variant={isStale ? 'destructive' : 'outline'} className="font-normal">
                <Clock className="mr-1 h-3 w-3" />
                Last rollup {staleMinutes}m ago
              </Badge>
            )}
            {snapshot?.partial && (
              <Badge variant="warning">
                Partial · {snapshot.partialSections?.join(', ') || 'unknown'}
              </Badge>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
            >
              <RefreshCw
                className={`mr-1 h-3.5 w-3.5 ${refresh.isPending ? 'animate-spin' : ''}`}
              />
              Refresh now
            </Button>
          </div>
        }
      />

      {metrics.isLoading && <LoadingState label="Loading dashboard" />}
      {metrics.isError && (
        <ErrorState
          title="Dashboard failed to load"
          description={humanizeError(metrics.error)}
          actionLabel="Retry"
          onAction={() => metrics.refetch()}
        />
      )}

      {!metrics.isLoading && !metrics.isError && !metrics.data?.snapshot && (
        <EmptyState
          icon={Activity}
          title="Awaiting first rollup"
          description="HealthMetricsRollupCron runs every 15 minutes. The first snapshot will appear shortly after the rollup completes."
        />
      )}

      {metrics.data?.snapshot && (
        <DashboardBody snapshot={metrics.data.snapshot} />
      )}

      {snapshot && <DeliveryFunnelCard snapshot={snapshot} />}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-4 w-4" /> Live decisions
          </CardTitle>
          <CardDescription>
            Newest decisions logged by the notification sender. Reload the page or hit
            &ldquo;Refresh now&rdquo; for fresh data — the list does not poll. Click any row for
            the full content + engagement breakdown.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 p-0">
          <DecisionsBrowser />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sends &amp; opens · last 14 days</CardTitle>
          <CardDescription>
            Aggregated live from the decision log. Timezone: UTC.
            {timeseries.data && !timeseries.data.timeseries.scannedAll && (
              <> Scan capped at 50k decisions — totals may understate volume.</>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {timeseries.isLoading && <LoadingState label="Loading timeseries" inline />}
          {timeseries.isError && (
            <ErrorState
              title="Timeseries unavailable"
              description={humanizeError(timeseries.error)}
            />
          )}
          {timeseries.data && (
            <TimeseriesChart
              series={buildTimeseriesSeries(timeseries.data.timeseries.days)}
              height={260}
              yFormatter={(v) => humanizeCount(v)}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function DeliveryFunnelCard({ snapshot }: { snapshot: NotificationDashboardSnapshot }) {
  const decisions = snapshot.totals.decisions;
  const sends = snapshot.totals.sends;
  const opens = snapshot.totals.receipts_opened;
  const sendRate = decisions > 0 ? sends / decisions : null;
  const openRate = sends > 0 ? opens / sends : null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Delivery funnel · 24h</CardTitle>
        <CardDescription>
          Decisions → sends → opens. Drop-offs highlight where the system is filtering.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <FunnelStep
            label="Decisions"
            value={humanizeCount(decisions)}
            hint="evaluated by Decision Brain"
          />
          <FunnelStep
            label="Sends"
            value={humanizeCount(sends)}
            hint={sendRate !== null ? `${humanizePercent(sendRate)} of decisions` : '—'}
            tone={sendRate !== null && sendRate < 0.3 ? 'warning' : 'default'}
          />
          <FunnelStep
            label="Opens"
            value={humanizeCount(opens)}
            hint={openRate !== null ? `${humanizePercent(openRate)} of sends` : '—'}
            tone={openRate !== null && openRate < 0.1 ? 'warning' : 'default'}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function FunnelStep({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: string;
  hint: string;
  tone?: 'default' | 'warning';
}) {
  return (
    <div
      className={`rounded-md border p-3 ${
        tone === 'warning' ? 'border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5' : ''
      }`}
    >
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="font-display text-2xl font-extrabold">{value}</div>
      <div className="text-xs text-muted-foreground">{hint}</div>
    </div>
  );
}

function DashboardBody({ snapshot }: { snapshot: NotificationDashboardSnapshot }) {
  const { totals, byCategory, bySkipReason, queueDepth, deferral, cronHeartbeats } = snapshot;
  const openRate =
    totals.sends > 0 ? totals.receipts_opened / totals.sends : null;

  const categoryStack = React.useMemo(() => {
    return Object.entries(byCategory).map(([cat, stat]) => ({
      category: cat,
      sends: stat?.sends ?? 0,
      opens: stat?.opens ?? 0,
    }));
  }, [byCategory]);

  const skipRows = React.useMemo(
    () =>
      Object.entries(bySkipReason)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 8),
    [bySkipReason],
  );

  return (
    <>
      {snapshot.kpis && (
        <section className="space-y-2">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Plan targets · rolling 7 days
            </h2>
            <span className="text-[11px] text-muted-foreground">
              Baselines + targets from <code>NOTIFICATION_SYSTEM_REDESIGN_PLAN.md</code> Part 6
            </span>
          </div>
          <KpiCards kpis={snapshot.kpis} />
        </section>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Sends · 24h"
          value={humanizeCount(totals.sends)}
          hint={`${humanizeCount(totals.decisions)} decisions`}
          icon={Activity}
        />
        <StatCard
          label="Open rate"
          value={openRate === null ? '—' : humanizePercent(openRate)}
          hint={`${humanizeCount(totals.receipts_opened)} opens / ${humanizeCount(totals.receipts_timeout)} timeout`}
          icon={Bell}
        />
        <StatCard
          label="Suppressions"
          value={humanizeCount(totals.suppressions)}
          hint="skipped or deferred"
          tone={totals.suppressions > totals.sends ? 'warning' : 'default'}
          icon={Ban}
        />
        <StatCard
          label="p99 queue depth"
          value={humanizeCount(queueDepth?.p99 ?? 0)}
          hint={`max ${humanizeCount(queueDepth?.max ?? 0)} · deferral p99 ${deferral?.p99Hours?.toFixed(1) ?? '0.0'}h`}
          icon={Clock}
          tone={queueDepth?.p99 > 500 ? 'destructive' : 'default'}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>By category</CardTitle>
            <CardDescription>Sends vs opens per category (last 24h).</CardDescription>
          </CardHeader>
          <CardContent>
            <StackedBarChart
              data={categoryStack}
              xKey="category"
              series={[
                { key: 'sends', label: 'Sends', color: '#4f46e5' },
                { key: 'opens', label: 'Opens', color: '#ff3366' },
              ]}
              height={240}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top skip reasons</CardTitle>
            <CardDescription>Why decisions did not become sends.</CardDescription>
          </CardHeader>
          <CardContent>
            {skipRows.length === 0 ? (
              <EmptyState title="No skips in window" description="Everything that passed the brain was sent." />
            ) : (
              <ul className="space-y-2">
                {skipRows.map(([reason, count]) => {
                  const max = Math.max(...skipRows.map(([, c]) => c));
                  const pct = max > 0 ? (count / max) * 100 : 0;
                  return (
                    <li key={reason} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-mono">{reason}</span>
                        <span className="text-muted-foreground">{humanizeCount(count)}</span>
                      </div>
                      <div
                        aria-hidden
                        className="h-1.5 overflow-hidden rounded-full bg-muted"
                      >
                        <div
                          className="h-full bg-primary transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Cron heartbeats</CardTitle>
          <CardDescription>
            Green = fresh (&lt;30m). Amber = stale. Red = silent (&gt;2h) — cron_silent alert policy should be firing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CronHeartbeatStrip heartbeats={cronHeartbeats} />
        </CardContent>
      </Card>
    </>
  );
}

function buildTimeseriesSeries(days: DecisionsTimeseriesDay[]) {
  return [
    {
      key: 'sends',
      label: 'Sends',
      color: '#4f46e5',
      values: days.map((d) => ({ ts: d.day, value: d.sends })),
    },
    {
      key: 'opens',
      label: 'Opens',
      color: '#ff3366',
      values: days.map((d) => ({ ts: d.day, value: d.opens })),
    },
    {
      key: 'suppressions',
      label: 'Suppressed',
      color: '#f59e0b',
      values: days.map((d) => ({ ts: d.day, value: d.suppressions })),
    },
  ];
}

